/** The workshop API client — the shared @starling-cloud/sturnia-workshop
 * WorkshopClient configured for scapescene, plus the scapescene-specific
 * helpers (asset keys). Everything in the app requests through here. */

import {
  ApiError,
  WorkshopClient,
  isPasswordError as libIsPasswordError,
  type RequestOptions,
} from "@starling-cloud/sturnia-workshop";

export { ApiError };

// Empty in dev (the Vite server proxies /api to the backend); the
// production build bakes in the API origin, because the widget is
// served from Object Storage — a different origin than the API.
const BASE = import.meta.env.VITE_API_BASE ?? "";

/** The one client. The shared workshop password is kept on the device
 * under "scapescene:password" and sent with every request; the backend
 * is what enforces it (the widget itself is a public file). */
export const client = new WorkshopClient({
  baseUrl: BASE,
  passwordKey: "scapescene:password",
  adminPasswordKey: "scapescene:admin-password",
});

/** Any cheap gated GET — what the password gate probes. */
export const PROBE_PATH = "/api/v1/definitions/";

/** An API path as a full URL — for resources the browser loads
 * directly (the QR <img>), which bypass request() and would otherwise
 * resolve against the widget's own origin, the bucket. */
export function apiUrl(path: string): string {
  return client.url(path);
}

/** The identity of a media URL: the path without its querystring.
 * Presigned bucket URLs carry a fresh signature on every poll while
 * pointing at the same object — any "did this asset change?" check
 * MUST compare assetKey(url), never the raw URL, or the UI reloads
 * (and visibly flickers) images and videos on every poll. */
export function assetKey(url: string): string {
  return url.split("?")[0];
}

export function storedPassword(): string {
  return client.storedPassword();
}

export function storePassword(password: string): void {
  client.storePassword(password);
}

export function clearPassword(): void {
  client.clearPassword();
}

export function isPasswordError(error: unknown): boolean {
  return libIsPasswordError(error);
}

export function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return client.request<T>(method, path, options);
}
